import React, { useState, useEffect } from 'react';
import {
  Card, CardContent, Typography, Box, TextField,
  Button, MenuItem, CircularProgress, Alert
} from '@mui/material';
import { contactPropertyService } from '../../services/contactPropertyService';

const ContactPropertyDetail = ({ property, projectId, onSaveSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    key: '',
    property_type: 0,
    status: 'active'
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const isDefault = property?.contact_property_type === 'default';

  useEffect(() => {
    if (property) {
      setFormData({
        name: property.name || '',
        key: property.key || '',
        property_type: property.property_type || 0,
        status: property.status || 'active'
      });
    } else {
      setFormData({ name: '', key: '', property_type: 0, status: 'active' });
    }
  }, [property]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (!formData.name.trim() || (!property?._id && !formData.key.trim())) {
      setError('Name and Key are required');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      const payload = { ...formData, project: projectId };

      let savedProp;
      if (property && property._id) {
        savedProp = await contactPropertyService.updateContactProperty(property._id, payload);
      } else {
        savedProp = await contactPropertyService.createContactProperty(payload);
      }

      if (onSaveSuccess) onSaveSuccess(savedProp, !property?._id);
    } catch (err) {
      console.error('Error saving contact property:', err);
      setError(err.response?.data?.message || 'Failed to save property');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card sx={{ height: '100%', overflowY: 'auto' }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {property?._id ? `Edit ${property.name}` : 'Add New Contact Property'}
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Box component="form" sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
          <TextField
            required
            fullWidth
            label="Name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            size="small"
            disabled={isDefault}
          />

          <TextField
            fullWidth
            select
            label="Type"
            name="property_type"
            value={formData.property_type}
            onChange={handleChange}
            size="small"
            disabled={isDefault}
          >
            <MenuItem value={0}>Text</MenuItem>
            <MenuItem value={1}>Date</MenuItem>
            <MenuItem value={2}>Number</MenuItem>
            <MenuItem value={3}>Boolean</MenuItem>
            <MenuItem value={4}>Array</MenuItem>
          </TextField>

          <TextField
            required
            fullWidth
            label="Key"
            name="key"
            value={formData.key}
            onChange={handleChange}
            size="small"
            disabled={!!property?._id} // Key is immutable after creation
            helperText={!property?._id ? "Unique identifier key (immutable)" : "Key cannot be changed"}
          />

          <TextField
            fullWidth
            select
            label="Status"
            name="status"
            value={formData.status}
            onChange={handleChange}
            size="small"
            disabled={isDefault}
          >
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </TextField>

          <Button
            variant="contained"
            color="success"
            onClick={handleSave}
            disabled={saving || isDefault}
            sx={{ alignSelf: 'flex-start' }}
          >
            {saving ? <CircularProgress size={24} color="inherit" /> : 'Save'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ContactPropertyDetail;
